/**
 * This hook and provider implementation are taken from the Transcend documentation.
 * @see https://docs.transcend.io/docs/articles/consent-management/reference/react-snippets
 */
import Script from 'next/script'
import { createContext, use, useEffect, useMemo, useState } from 'react'
import {
  type AirgapAPI,
  type PreInitTranscendAPI,
  type TranscendAPI,
} from '@transcend-io/airgap.js-types'
import { logError } from '@/features/shared/utils/logger'
import { TRANSCEND_CONSENT_LS_KEY } from '@shared/constants/transcend'
import { getItem, setItem } from '@/features/shared/utils/localStorage'
import { REGIME_COPIES, REGIMES } from '@/lib/transcend/constants'
import { usePathname } from 'next/navigation'
import { pciRouteMapByURL } from '@shared/constants/routes.pci'
import { isLocal } from '@/features/shared/utils/environment'

type ConsentValueAPI = boolean | 'Auto'

export type ConsentPurposesAPI = {
  Analytics?: ConsentValueAPI
  Advertising?: ConsentValueAPI
  Functional?: ConsentValueAPI
  SaleOfInfo?: ConsentValueAPI
}
export type ConsentPurposeAPI = keyof ConsentPurposesAPI

export type ConsentPurposes = {
  analytics: boolean
  advertising: boolean
  functional: boolean
}
export type ConsentPurpose = keyof ConsentPurposes

/** A stub for `window.airgap` to be used before airgap is actually loaded */
type PreInitAirgapAPI = Required<Pick<AirgapAPI, 'readyQueue' | 'ready'>>

/**
 * Add `window.airgap` and `window.transcend` to the global namespace
 */
declare global {
  interface Window {
    /** airgap.js interface */
    airgap?: PreInitAirgapAPI | AirgapAPI
    /** Transcend consent manager interface */
    transcend?: PreInitTranscendAPI | TranscendAPI
  }
}

/**
 * The `airgap` and `transcend` consent management APIs
 *
 * @example const { airgap, transcend } = useConsentManager();
 */
interface ConsentAPI {
  /** The `airgap` consent management instance */
  airgap?: AirgapAPI
  /** The `transcend` consent manager UI instance */
  transcend?: TranscendAPI
}

interface ConsentProviderProps {
  /** The children of this context provider */
  children: React.ReactNode
}

/** The React context by the `useConsentManager()` hook */
const ConsentContext = createContext<ConsentAPI>({})

/**
 * React context provider for `window.airgap` and `window.transcend`
 * @see https://docs.transcend.io/docs/consent/faq
 */
export const ConsentProvider = ({ children }: ConsentProviderProps) => {
  const [airgap, setAirgap] = useState<AirgapAPI | undefined>()
  const [transcend, setTranscend] = useState<TranscendAPI | undefined>()
  const pathname = usePathname() ?? ''

  // `useEffect` ensures this is only executed in browser
  useEffect(() => {
    try {
      // Stub transcend with PreInit API
      if (!self.transcend?.ready) {
        const preInitTranscend: PreInitTranscendAPI = {
          readyQueue: [],
          ready(callback) {
            preInitTranscend.readyQueue.push(callback)
          },
          ...self.transcend,
        }
        self.transcend = preInitTranscend
      }

      // Stub airgap with PreInit API
      if (!self.airgap?.ready) {
        const preInitAirgap: PreInitAirgapAPI = {
          readyQueue: [],
          ready(callback) {
            preInitAirgap.readyQueue.push(callback)
          },
          ...self.airgap,
        }
        self.airgap = preInitAirgap
      }

      // Wait for consent manager UI to load, and set it in the React state
      if (!transcend) {
        self.transcend.ready((transcendInstance) => {
          setTranscend(transcendInstance)
          if (!airgap) return
          const regimes = airgap.getRegimes()
          // Based on the user's detected regime / location, we customize the consent banner copy for Virginia and other US states. And a consistent UI design for Virginia and other states is maintained.
          if ([...regimes][0] === REGIMES.OTHER_STATES) {
            transcendInstance.setUiVariables({
              siteDependedVariable: REGIME_COPIES[REGIMES.OTHER_STATES].siteDependedVariable,
            })
          } else if ([...regimes][0] === REGIMES.VIRGINIA_USERS) {
            transcendInstance.setUiVariables({
              siteDependedVariable: REGIME_COPIES[REGIMES.VIRGINIA_USERS].siteDependedVariable,
            })
          }
        })
      }

      // Wait for airgap.js core to load, and set it in the React state
      if (!airgap) {
        self.airgap.ready((airgapInstance) => {
          setAirgap(airgapInstance)
          // Store consent values in localStorage when airgap initializes, so LS persists if user hasnt updated consent banner.
          if (!getItem(TRANSCEND_CONSENT_LS_KEY)) {
            try {
              const consentValues = airgapInstance.getConsent()
              setItem(TRANSCEND_CONSENT_LS_KEY, JSON.stringify(consentValues))
            } catch (err) {
              logError(
                new Error('Failed to save tcmConsent to localStorage', {
                  cause: err,
                }),
              )
            }
          }
        })
      }
    } catch (e) {
      logError(new Error('Was unable to load Transcend airgap script', { cause: e }))
    }
  }, [airgap, transcend])

  const contextValues = useMemo(() => ({ airgap, transcend }), [airgap, transcend])

  return (
    <>
      {!pciRouteMapByURL.has(pathname) && (
        <Script
          src={`${process.env.NEXT_PUBLIC_TRANSCEND_URL}/${process.env.NEXT_PUBLIC_TRANSCEND_BUNDLE_ID}/airgap.js`}
          data-bup="off"
          // disable data protection for local dev env since it causes hyddration issues when streaming.
          // this happens since local dev env compares every attribute in the browser's HTML against the props react
          // rendered. an attribute react didn't render, like data-airgap-id, gets reported as a hydration error
          // but it is not an issue in prod since production hydration only fails on structural or text differences.
          data-protect={isLocal ? 'off' : 'on'}
          data-more-choices-view="CompleteOptionsToggles"
        />
      )}
      <ConsentContext value={contextValues}>{children}</ConsentContext>
    </>
  )
}

export const useConsentManager = (): ConsentAPI => use(ConsentContext)
